import * as React from "react";
import { useEditor, EditorContent, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Image } from "@tiptap/extension-image";
import { Link } from "@tiptap/extension-link";
import { Placeholder } from "@tiptap/extension-placeholder";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { TextAlign } from "@tiptap/extension-text-align";
import { Underline } from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { Highlight } from "@tiptap/extension-highlight";
import { FontFamily } from "@tiptap/extension-font-family";
import useApi from "@/hooks/useApi";

export type RichEditorMode = "minimal" | "standard" | "full";

interface RichEditorProps {
  value: string;
  onChange: (value: string) => void;
  uploadUrl?: string;
  mode?: RichEditorMode;
  placeholder?: string;
  className?: string;
  minHeight?: string;
}

const FONT_FAMILIES = [
  { label: "Default",  value: "" },
  { label: "Arial",    value: "Arial, sans-serif" },
  { label: "Georgia",  value: "Georgia, serif" },
  { label: "Verdana",  value: "Verdana, sans-serif" },
  { label: "Courier",  value: "'Courier New', monospace" },
  { label: "Trebuchet",value: "'Trebuchet MS', sans-serif" },
];

function Btn({
  onClick, active, title, disabled, children,
}: {
  onClick: () => void; active?: boolean; title?: string; disabled?: boolean; children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onMouseDown={e => { e.preventDefault(); onClick(); }}
      title={title}
      disabled={disabled}
      className={`px-2 py-1 rounded text-sm transition-colors ${
        active
          ? "bg-[var(--color-primary)] text-white"
          : "text-slate-600 hover:bg-slate-100 hover:text-slate-800"
      } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
    >
      {children}
    </button>
  );
}

function Sep() {
  return <div className="w-px bg-slate-200 self-stretch mx-0.5" />;
}

function MinimalToolbar({ editor }: { editor: Editor }) {
  const addLink = () => {
    const url = window.prompt("URL:", editor.getAttributes("link").href ?? "https://");
    if (url === null) return;
    if (!url) editor.chain().focus().extendMarkRange("link").unsetLink().run();
    else editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-slate-200 bg-slate-50">
      <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold"><strong>B</strong></Btn>
      <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic"><em>I</em></Btn>
      <Btn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="Underline"><u>U</u></Btn>
      <Btn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title="Strikethrough"><s>S</s></Btn>
      <Sep />
      <Btn onClick={addLink} active={editor.isActive("link")} title="Link"><i className="fa fa-link text-xs" /></Btn>
      <Sep />
      <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bullet list"><i className="fa fa-list-ul text-xs" /></Btn>
      <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered list"><i className="fa fa-list-ol text-xs" /></Btn>
      <Sep />
      <Btn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo"><i className="fa fa-undo text-xs" /></Btn>
      <Btn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo"><i className="fa fa-redo text-xs" /></Btn>
    </div>
  );
}

function FullToolbar({ editor, uploadUrl }: { editor: Editor; uploadUrl?: string }) {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = React.useState(false);
  const api = useApi();

  const addLink = () => {
    const url = window.prompt("URL:", editor.getAttributes("link").href ?? "https://");
    if (url === null) return;
    if (!url) editor.chain().focus().extendMarkRange("link").unsetLink().run();
    else editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const handleImageUpload = async (file: File) => {
    if (!uploadUrl) return;
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const resp = await api.admin.postFormData(uploadUrl, formData);
      if (resp.ok) {
        const data = await resp.json();
        const url = data?.url ?? data?.default;
        if (url) editor.chain().focus().setImage({ src: url }).run();
      }
    } catch { /* non-fatal */ } finally {
      setIsUploading(false);
    }
  };

  const headingValue =
    editor.isActive("heading", { level: 1 }) ? "h1"
    : editor.isActive("heading", { level: 2 }) ? "h2"
    : editor.isActive("heading", { level: 3 }) ? "h3"
    : "p";

  const currentFont = editor.getAttributes("textStyle").fontFamily ?? "";

  return (
    <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-slate-200 bg-slate-50 sticky top-0 z-10">
      {/* Heading */}
      <select
        className="text-sm border border-slate-200 rounded px-1 py-0.5 bg-white text-slate-700 mr-0.5"
        value={headingValue}
        onChange={e => {
          const v = e.target.value;
          if (v === "p") editor.chain().focus().setParagraph().run();
          else editor.chain().focus().toggleHeading({ level: parseInt(v[1]) as 1|2|3 }).run();
        }}
      >
        <option value="p">Paragraph</option>
        <option value="h1">Heading 1</option>
        <option value="h2">Heading 2</option>
        <option value="h3">Heading 3</option>
      </select>

      {/* Font family */}
      <select
        className="text-sm border border-slate-200 rounded px-1 py-0.5 bg-white text-slate-700 mr-0.5"
        value={currentFont}
        onChange={e => {
          const chain = editor.chain().focus() as any;
          if (!e.target.value) chain.unsetFontFamily().run();
          else chain.setFontFamily(e.target.value).run();
        }}
      >
        {FONT_FAMILIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
      </select>

      <Sep />

      {/* Inline marks */}
      <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold"><strong>B</strong></Btn>
      <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic"><em>I</em></Btn>
      <Btn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="Underline"><u>U</u></Btn>
      <Btn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title="Strikethrough"><s>S</s></Btn>
      <Btn onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive("code")} title="Inline code"><code className="text-xs">`</code></Btn>

      <Sep />

      {/* Color */}
      <label title="Text color" className="px-1 py-0.5 rounded cursor-pointer hover:bg-slate-100 flex items-center gap-0.5 text-xs text-slate-600">
        <span>A</span>
        <input type="color" className="w-4 h-4 border-0 p-0 cursor-pointer rounded"
          value={editor.getAttributes("textStyle").color ?? "#000000"}
          onChange={e => (editor.chain().focus() as any).setColor(e.target.value).run()} />
      </label>
      <label title="Highlight" className="px-1 py-0.5 rounded cursor-pointer hover:bg-slate-100 flex items-center gap-0.5 text-xs text-slate-600">
        <i className="fa fa-highlighter text-xs" />
        <input type="color" className="w-4 h-4 border-0 p-0 cursor-pointer rounded"
          value={typeof editor.getAttributes("highlight").color === "string"
            ? editor.getAttributes("highlight").color : "#ffff00"}
          onChange={e => (editor.chain().focus() as any).toggleHighlight({ color: e.target.value }).run()} />
      </label>

      <Sep />

      {/* Alignment */}
      <Btn onClick={() => editor.chain().focus().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} title="Left"><i className="fa fa-align-left text-xs" /></Btn>
      <Btn onClick={() => editor.chain().focus().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} title="Center"><i className="fa fa-align-center text-xs" /></Btn>
      <Btn onClick={() => editor.chain().focus().setTextAlign("right").run()} active={editor.isActive({ textAlign: "right" })} title="Right"><i className="fa fa-align-right text-xs" /></Btn>
      <Btn onClick={() => editor.chain().focus().setTextAlign("justify").run()} active={editor.isActive({ textAlign: "justify" })} title="Justify"><i className="fa fa-align-justify text-xs" /></Btn>

      <Sep />

      {/* Lists */}
      <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bullet list"><i className="fa fa-list-ul text-xs" /></Btn>
      <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered list"><i className="fa fa-list-ol text-xs" /></Btn>

      <Sep />

      {/* Blocks */}
      <Btn onClick={addLink} active={editor.isActive("link")} title="Link"><i className="fa fa-link text-xs" /></Btn>
      {uploadUrl && (
        <Btn onClick={() => fileInputRef.current?.click()} title="Upload image" disabled={isUploading}>
          {isUploading
            ? <i className="fa fa-circle-notch fa-spin text-xs" />
            : <i className="fa fa-image text-xs" />}
        </Btn>
      )}
      <Btn onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title="Insert table"><i className="fa fa-table text-xs" /></Btn>
      <Btn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Blockquote"><i className="fa fa-quote-right text-xs" /></Btn>
      <Btn onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive("codeBlock")} title="Code block"><i className="fa fa-code text-xs" /></Btn>
      <Btn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Horizontal line"><i className="fa fa-minus text-xs" /></Btn>

      <Sep />

      <Btn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo"><i className="fa fa-undo text-xs" /></Btn>
      <Btn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo"><i className="fa fa-redo text-xs" /></Btn>

      {uploadUrl && (
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
          onChange={e => { const f = e.target.files?.[0]; if (f) handleImageUpload(f); e.target.value = ""; }} />
      )}
    </div>
  );
}

export default function RichEditor({
  value, onChange, uploadUrl, mode = "standard", placeholder, className = "", minHeight = "120px",
}: RichEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        codeBlock: { HTMLAttributes: { class: "rounded bg-slate-100 p-4 font-mono text-sm" } },
      }),
      Underline,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      ...(mode === "full" ? [FontFamily] : []),
      ...(mode !== "minimal" ? [TextAlign.configure({ types: ["heading", "paragraph"] })] : []),
      ...(mode === "full" ? [
        Image.configure({ inline: false, allowBase64: false }),
        Table.configure({ resizable: true }),
        TableRow,
        TableHeader,
        TableCell,
      ] : []),
      Link.configure({ openOnClick: false, autolink: true }),
      Placeholder.configure({ placeholder: placeholder ?? "Start typing…" }),
    ],
    content: value,
    onUpdate({ editor }) {
      onChange(editor.getHTML());
    },
  });

  const prevValue = React.useRef(value);
  React.useEffect(() => {
    if (!editor) return;
    if (value !== prevValue.current && value !== editor.getHTML()) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
    prevValue.current = value;
  }, [value, editor]);

  if (!editor) return null;

  return (
    <div className={`flex flex-col border border-slate-300 rounded-lg overflow-hidden bg-white ${className}`}>
      {mode === "minimal"
        ? <MinimalToolbar editor={editor} />
        : <FullToolbar editor={editor} uploadUrl={uploadUrl} />}
      <EditorContent
        editor={editor}
        className="tiptap-editor flex-1 px-3 py-2"
        style={{ minHeight }}
      />
    </div>
  );
}
